/* eslint-disable complexity */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SystemRolesName } from 'src/api';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { Modal } from 'src/components/molecules';
import { UploadScreen } from 'src/components/pages/UploadUsers';
import {
  BulkReinviteExistingUser,
  BulkReinviteNewUser,
  useMeQuery,
} from 'src/graphql';
import { useCalculateTableRows } from 'src/hooks';
import { FeatureFlag, State } from 'src/types';
import {
  trackBulkActivationsReviewed,
  trackBulkChangesCancelled,
  trackBulkChangesConfirmed,
  trackBulkDeactivationsReviewed,
} from 'src/utils/analytics';
import { PaginatedTable } from '../PaginatedTable';
import { Bold, List, ListItem, TableContainer } from './UploadActions.styles';
import { useUploadActions } from './useUploadActions';

interface Props {
  screen: UploadScreen;
  totalUsersInCSV: number;
  usersToReinvite: BulkReinviteExistingUser[];
  usersToCreate: BulkReinviteNewUser[];
  usersToDeactivate: BulkReinviteExistingUser[];
  usersToUpdate: BulkReinviteExistingUser[];
  setScreen: (screen: UploadScreen) => void;
  orgExpiryPeriod: number;
  commit: (activate: boolean, deactivate: boolean) => void;
  setShowCancellationModal: React.Dispatch<React.SetStateAction<boolean>>;
  setPreventNavigation: (prevent: boolean) => void;
  preventNavigation: boolean;
  setRedirect: (redirect: boolean) => void;
}

// eslint-disable-next-line complexity
export const UploadActions = ({
  usersToCreate,
  usersToReinvite,
  usersToDeactivate,
  usersToUpdate,
  setScreen,
  screen,
  setShowCancellationModal,
  preventNavigation,
  setPreventNavigation,
  commit,
  setRedirect,
}: Props) => {
  const { data: callingUser } = useMeQuery();

  // Using roles directly here rather than system_permissions as its for tracking, not permission checks
  const is_admin =
    callingUser?.me?.accounts?.some((account) =>
      account?.Roles?.some((role) => role.name === SystemRolesName.ADMIN),
    ) ?? false;
  const is_super_admin =
    callingUser?.me?.accounts?.some((account) =>
      account?.Roles?.some((role) => role.name === SystemRolesName.SUPER),
    ) ?? false;

  const organisation_id = callingUser?.me.organisation
    ? String(callingUser?.me.organisation)
    : null;

  const {
    screenIsActivate,
    numberOfUsersToInvite,
    showConfirmationModal,
    setShowConfirmationModal,
    showActivationModal,
    setShowActivationModal,
    showDeactivationModal,
    setShowDeactivationModal,
    activateToggleOn,
    deactivateToggleOn,
    ActivateTableInfo,
    DeactivateTableInfo,
    sortedTableUsers,
    columns,
    handleColumnSort,
    sortedColumn,
    activateWithoutInvite,
  } = useUploadActions(
    screen,
    usersToCreate,
    usersToReinvite,
    usersToDeactivate,
  );

  const features = useSelector(({ features }: State) => features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const orgExpiryPeriod = useSelector(
    ({ organisation }: State) => organisation?.organisation?.expiryPeriod ?? 90,
  );

  useEffect(() => {
    if (!preventNavigation) {
      setScreen(UploadScreen.UPLOAD);
    }
    setPreventNavigation(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preventNavigation, screenIsActivate, setPreventNavigation]);

  const noUsersToDeactivate = usersToDeactivate.length === 0;

  useEffect(() => {
    if (!screenIsActivate && noUsersToDeactivate) {
      setShowConfirmationModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenIsActivate, noUsersToDeactivate]);

  const noChangesNeeded =
    usersToUpdate.length === 0 &&
    numberOfUsersToInvite === 0 &&
    (!deactivateToggleOn || noUsersToDeactivate);

  // If no users to create or reinvite, then the top of table is smaller
  const extraPadding =
    screenIsActivate &&
    ((usersToCreate.length === 0 && usersToReinvite.length !== 0) ||
      (usersToCreate.length !== 0 && usersToReinvite.length === 0));

  const pagePadding = 40;
  const stepperHeight = 60;
  const panelMargin = 30;
  const panelTopHeight = extraPadding ? 132 : 155;
  const paginationHeight = 56;
  const tableHeaderHeight = 56;

  const minPanelHeight = 768;
  const rowHeight = 56;
  const minRows = 7;

  const minPanelWidth = 980;

  const panelExcludingTable = panelTopHeight + paginationHeight;
  const pageExcludingPanel = pagePadding + stepperHeight + panelMargin;

  const { rows } = useCalculateTableRows(
    rowHeight,
    minRows,
    pageExcludingPanel + panelExcludingTable + tableHeaderHeight,
  );

  const minTableHeight = minPanelHeight - panelExcludingTable;
  const tableHeight = `calc(100vh - ${
    pageExcludingPanel + panelExcludingTable - tableHeaderHeight
  }px)`;

  const minTableWidth = minPanelWidth;

  return (
    <>
      <TableContainer>
        {screenIsActivate ? ActivateTableInfo : DeactivateTableInfo}
        <PaginatedTable
          sortableColumns={[
            'Full name',
            'Email',
            'Role',
            'Department',
            'Site',
            'Country',
            'Termination Date',
          ]}
          minWidth={minTableWidth}
          minHeight={minTableHeight}
          height={tableHeight}
          buttons={{
            left: {
              onClick: () => {
                if (numberOfUsersToInvite === 0 || screenIsActivate) {
                  setShowCancellationModal(true);
                } else {
                  setScreen(UploadScreen.ACTIVATE);
                }
              },
              text:
                numberOfUsersToInvite === 0 && !screenIsActivate
                  ? 'Cancel'
                  : 'Back',
            },
            right: {
              onClick: () => {
                if (screenIsActivate) {
                  if (segmentEnabled && organisation_id) {
                    trackBulkActivationsReviewed({
                      organisation_id,
                      invite_toggle: activateToggleOn,
                      is_admin,
                      is_super_admin,
                    });
                  }
                  setScreen(UploadScreen.DEACTIVATE);
                } else {
                  if (segmentEnabled && organisation_id) {
                    trackBulkDeactivationsReviewed({
                      organisation_id,
                      deactivate_toggle: deactivateToggleOn,
                      is_admin,
                      is_super_admin,
                    });
                  }
                  setShowConfirmationModal(true);
                }
              },
              text: 'Next',
            },
          }}
          tableData={sortedTableUsers}
          columns={columns}
          visibleColumns={columns}
          onColumnSort={handleColumnSort}
          data-testid="upload-actions-table"
          maxRows={rows < minRows ? minRows : rows}
          rowHeight={rowHeight}
          isScrollable={true}
          fixedRowHeight={true}
          sortedColumn={sortedColumn}
          emptyText="No users to display"
        />
      </TableContainer>
      <Modal
        open={showActivationModal}
        setOpen={setShowActivationModal}
        onConfirm={() => setShowActivationModal(false)}
        size="medium"
        title="What does activation mean?"
        description="Activation means adding employees into your User Database and granting access to the Vault Reporting App."
      />
      <Modal
        open={showDeactivationModal}
        setOpen={setShowDeactivationModal}
        onConfirm={() => setShowDeactivationModal(false)}
        size="medium"
        title="What does deactivation mean?"
        description={`Deactivation means revoking access to Vault App for the identified employees, ${orgExpiryPeriod} days from now.`}
        data-testid="deactivate-modal"
      />
      {noChangesNeeded ? (
        <Modal
          open={showConfirmationModal}
          setOpen={setShowConfirmationModal}
          onConfirm={() => {
            if (segmentEnabled && organisation_id) {
              trackBulkChangesConfirmed({
                organisation_id,
                is_admin,
                is_super_admin,
              });
            }
            setShowConfirmationModal(false);
            setRedirect(true);
          }}
          onCancel={() => {
            if (segmentEnabled && organisation_id) {
              trackBulkChangesCancelled({
                changes: [],
                is_admin,
                is_super_admin,
                organisation_id,
              });
            }
            setShowConfirmationModal(false);
          }}
          size="medium"
          title="No changes needed"
          description="Your database of users is currently up-to-date."
          data-testid="no-changes-needed-modal"
        />
      ) : (
        <Modal
          open={showConfirmationModal}
          setOpen={setShowConfirmationModal}
          onConfirm={() => {
            try {
              setShowConfirmationModal(false);
              commit(activateToggleOn, deactivateToggleOn);
              if (segmentEnabled && organisation_id) {
                trackBulkChangesConfirmed({
                  organisation_id,
                  is_admin,
                  is_super_admin,
                });
              }
              setRedirect(true);
            } catch (error) {
              setShowConfirmationModal(false);
              setRedirect(true);
            }
          }}
          onCancel={() => {
            if (segmentEnabled && organisation_id) {
              trackBulkChangesCancelled({
                changes: [
                  usersToCreate.length !== 0 || usersToReinvite.length !== 0
                    ? 'Invited'
                    : '',
                  usersToDeactivate.length !== 0 ? 'Deactivated' : '',
                  usersToUpdate.length !== 0 ? 'Updated' : '',
                ].filter((change) => change !== ''),
                is_admin,
                is_super_admin,
                organisation_id,
              });
            }
            setShowConfirmationModal(false);
          }}
          size="medium"
          title="Confirm updates"
          description="You are making changes to your User Database with the following updates:"
          data-testid="confirm-updates-modal"
        >
          <List>
            {usersToUpdate.length > 0 && (
              <ListItem>
                <Bold>{usersToUpdate.length}</Bold>
                {` ${
                  usersToUpdate.length === 1 ? `employee's` : `employees'`
                } details will be `}
                <Bold>updated</Bold>
              </ListItem>
            )}
            {numberOfUsersToInvite > 0 && (
              <ListItem>
                <Bold>{numberOfUsersToInvite}</Bold>
                {` ${
                  numberOfUsersToInvite === 1 ? `employee` : `employees`
                } will be `}
                <Bold>
                  {activateWithoutInvite
                    ? 'activated'
                    : activateToggleOn
                      ? 'invited'
                      : 'created'}
                </Bold>
              </ListItem>
            )}
            {deactivateToggleOn && !noUsersToDeactivate && (
              <ListItem>
                <Bold>{usersToDeactivate.length}</Bold>
                {` ${
                  usersToDeactivate.length === 1 ? `employee` : `employees`
                } will be `}
                <Bold>deactivated</Bold>
              </ListItem>
            )}
          </List>
        </Modal>
      )}
    </>
  );
};
